import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers, createSlots as _createSlots, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pr-2 py-1 text-sm"
}
const _hoisted_2 = { class: "font-weight-bold mr-1" }
const _hoisted_3 = { class: "sticky-add-new" }
const _hoisted_4 = { class: "py-1 text-sm" }
const _hoisted_5 = { class: "font-weight-bold mr-1" }
const _hoisted_6 = { class: "sticky-add-new" }

import { storeToRefs } from 'pinia';
import { onMounted, onBeforeUnmount, reactive, computed, watch, ref, type PropType } from 'vue';
import useClientMatterOptionsStore from '@/services/useClientMatterOptionsStore';
import debounce from '@/components/shared/debounce';
import LlIcon from '@/components/LlIcon.vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useAbortController } from '@/composables/useAbortController';
import { ClientMatterSearchState } from '@/components/models/clientMatterSearchState';
import { ClientOptionModel } from '@/models/clientOptionModel';
import { MatterOptionModel } from '@/models/matterOptionModel';
import { ClientMatter } from '@/models/clientMatter';
import type { ClientTableFilter, MatterTableFilter } from '@/models/tableFilters';

const itemSearchThreshold = 200;
const UI_DELAY_MS = 50; // Delay for UI state updates and focus management

// Abort controller composable for managing request cancellation

export default /*@__PURE__*/_defineComponent({
  __name: 'ClientMatterSearch',
  props: {
  horizontal: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: false,
  },
  emitOnAnyChange: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: false,
  },
  cm: {
    type: Object as PropType<ClientMatter>,
    required: true,
  },
  queryFilter: {
    type: Object as PropType<ClientTableFilter | MatterTableFilter>,
    required: true,
  },
  matterRules: {
    type: Array as PropType<Array<boolean | string>>,
    required: false,
    default: () => [],
  },
  clientRules: {
    type: Array as PropType<Array<boolean | string>>,
    required: false,
    default: () => [],
  },
  hideDetails: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: true,
  },
  required: {
    type: Boolean as PropType<Boolean>,
    default: false,
  },
  clientOnly: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: false,
  },
  hideClientLabel: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: false,
  },
  showAddClientMatter: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean as PropType<Boolean>,
    required: false,
    default: false,
  },
  // Test hooks. Default to the names the Cypress suite already uses everywhere; a page that
  // adopted this picker after its own fields were already under test can keep its own.
  clientDataCy: {
    type: String as PropType<string>,
    required: false,
    default: 'client-search',
  },
  matterDataCy: {
    type: String as PropType<string>,
    required: false,
    default: 'matter-search',
  },
},
  emits: ['matterChange', 'addNewClient', 'addNewMatter'],
  setup(__props, { expose: __expose, emit: __emit }) {

const meStore = useMeStore();
const cmOptionsStore = new useClientMatterOptionsStore();
const emit = __emit;
const { terms } = storeToRefs(useMeStore());

const props = __props;

const abortController = useAbortController();

const root = ref<HTMLElement | null>(null);

const vm: ClientMatterSearchState = reactive({
  clientsLoading: false,
  mattersLoading: false,
  clients: [],
  matters: [],
  clientQuery: {
    ...props.queryFilter,
    search: '',
  },
  matterQuery: {
    ...props.queryFilter,
    search: '',
    clientId: props.cm.client?.id,
  },
  clearingMatterSearch: false,
  clientMenuOpen: false,
  matterMenuOpen: false,
  clientHighlightedIndex: -1,
  matterHighlightedIndex: -1,
  clientFocused: false,
  matterFocused: false,
});

const useClientSearch = computed(() => {
  return cmOptionsStore.clientCount > itemSearchThreshold;
});

const useMatterSearch = computed(() => {
  return cmOptionsStore.matterCount > itemSearchThreshold;
});

const clientNoDataMessage = computed(() => {
  if (vm.clientsLoading) {
    return 'Searching clients...';
  }
  if (
    vm.clientQuery.search?.length === 0 &&
    cmOptionsStore.clientCount > itemSearchThreshold &&
    vm.clients.length === 0
  ) {
    return cmOptionsStore.clientCount + ' results: type to search';
  }
  if (vm.clientQuery.search?.length > 0 && vm.clients.length === 0) {
    if (cmOptionsStore.clientCount > itemSearchThreshold) {
      return cmOptionsStore.clientCount + ' results: refine your search';
    }
    return 'No results, please modify your search';
  }
  return 'No results, please modify your search';
});

const matterNoDataMessage = computed(() => {
  if (vm.mattersLoading) {
    return 'Searching matters...';
  }
  if (
    vm.matterQuery.search?.length === 0 &&
    cmOptionsStore.matterCount > itemSearchThreshold &&
    vm.matters.length === 0
  ) {
    return cmOptionsStore.matterCount + ' results: type to search';
  }
  if (vm.matterQuery.search?.length > 0 && vm.matters.length === 0) {
    if (cmOptionsStore.matterCount > itemSearchThreshold) {
      return cmOptionsStore.matterCount + ' results: refine your search';
    }
    return 'No results, please modify your search';
  }
  return 'No results, please modify your search';
});

// Restarts the debounce on every keystroke and decides once typing settles: narrowing the
// loaded list filters it locally, anything else searches the server with the settled text.
const settledSearch = (
  loaded: () => unknown[],
  search: (text: string, from: string) => Promise<void>,
) => {
  let from: string | null = null; // the text before this burst of typing
  const run = debounce((text: string) => {
    const before = from ?? '';
    from = null;
    const list = loaded();
    if (list.length > 0 && list.length < itemSearchThreshold && text.indexOf(before) > -1) {
      return;
    }
    search(text, before);
  }, 300);
  return {
    onInput: (text: string, previous: string) => {
      if (vm.clearingMatterSearch) {
        return;
      }
      if (from === null) {
        from = previous;
      }
      run(text);
    },
    cancel: () => run.cancel(),
  };
};

const clientSearchInput = settledSearch(() => vm.clients, (text, from) => clientSearch(text, from));
const matterSearchInput = settledSearch(() => vm.matters, (text, from) => matterSearch(text, from));

watch(
  () => props.queryFilter,
  async (newVal, oldVal) => {
    vm.clientQuery = { ...newVal, search: vm.clientQuery.search };
    vm.matterQuery = {
      ...newVal,
      search: vm.matterQuery.search,
      clientId: vm.matterQuery.clientId,
    };
    if (vm.clearingMatterSearch) {
      return;
    }
    if (!props.cm.client?.id && !props.cm.matter?.id) {
      await fetchClients();
      await fetchMatters();
    }
    if (!oldVal) return;
    if (newVal !== oldVal && props.cm.client?.id) {
      vm.matterQuery.search = '';
      await fetchMatters();
    }
  },
);

watch(
  () => props.cm.client,
  async (newVal, oldVal) => {
    if (vm.clearingMatterSearch) {
      return;
    }
    if (newVal?.id === oldVal?.id) {
      return;
    }
    if (newVal?.id) {
      // Cancel previous client details request and create new one
      abortController.create('clientDetails');
      
      try {
        const client = await cmOptionsStore.getClient(
          newVal?.id,
          abortController.getSignal('clientDetails')
        );
        props.cm.client = client;
        vm.matterQuery.clientId = newVal?.id;
      } catch (error) {
        if (error.name === 'CanceledError') {
          return;
        }
        throw error;
      }
    }
  },
);

watch(() => vm.matterQuery.search, matterSearchInput.onInput);

watch(() => vm.clientQuery.search, clientSearchInput.onInput);

onMounted(async () => {
  await meStore.getMe();
  
  // Add global keyboard listener for dropdown navigation (capture phase needed for Tab)
  document.addEventListener('keydown', handleGlobalKeydown, true);
  
  if (props.cm.client?.id) {
    // Fetch client list for dropdown but preserve existing selections
    fetchClients(true);
    abortController.create('clientDetails');
    try {
      const client = await cmOptionsStore.getClient(
        props.cm.client.id,
        abortController.getSignal('clientDetails')
      );
      props.cm.client = client;
      await fetchMatters(true);
    } catch (error) {
      if (error.name === 'CanceledError') {
        return;
      }
      throw error;
    }
  } else {
    // No existing selections, safe to reset everything
    fetchClients(false);
    fetchMatters();
  }
});

onBeforeUnmount(() => {
  // Clean up global event listener
  document.removeEventListener('keydown', handleGlobalKeydown, true);
  // Cancel any pending debounced searches so they don't fire after teardown
  clientSearchInput.cancel();
  matterSearchInput.cancel();
});


const clickClient = async () => {};

const clickMatter = async () => {};

const onClientMenuUpdate = (isOpen: boolean): void => {
  vm.clientMenuOpen = isOpen;
  if (isOpen) {
    vm.clientHighlightedIndex = -1; // Reset when menu opens
  }
};

const onMatterMenuUpdate = (isOpen: boolean): void => {
  vm.matterMenuOpen = isOpen;
  if (isOpen) {
    vm.matterHighlightedIndex = -1; // Reset when menu opens
  }
};

const onClientFocus = (): void => {
  vm.clientFocused = true;
};

const onMatterFocus = (): void => {
  vm.matterFocused = true;
};

const onClientBlur = async () => {
  vm.clientFocused = false;
  
  // Using a small delay to prevent blur from triggering during menu interactions
  setTimeout(async () => {
    // Only clear if menu is closed, there's search text, and no selection was made
    if (!vm.clientMenuOpen && vm.clientQuery.search && !props.cm.client) {
      vm.clientQuery.search = '';
      // If we have a small number of clients, reload the full list
      if (cmOptionsStore.clientCount < itemSearchThreshold) {
        await fetchClients();
      } else {
        // For large lists, clear the dropdown to show clean state
        vm.clients = [];
      }
    }
  }, UI_DELAY_MS);
};

const onMatterBlur = async () => {
  vm.matterFocused = false;

  // Using a small delay to prevent blur from triggering during menu interactions
  setTimeout(async () => {
    // Only clear if menu is closed, there's search text, and no selection was made
    if (!vm.matterMenuOpen && vm.matterQuery.search && !props.cm.matter) {
      vm.matterQuery.search = '';
      // If we have a small number of matters, reload the full list
      if (cmOptionsStore.matterCount < itemSearchThreshold) {
        await fetchMatters();
      } else {
        // For large lists, clear the dropdown to show clean state
        vm.matters = [];
      }
    }
  }, UI_DELAY_MS);
};

const handleGlobalKeydown = (event: KeyboardEvent): void => {
  // Handle Tab key specially - check if either menu is open regardless of focus state
  if (event.key === 'Tab') {
    if (vm.clientMenuOpen && vm.clients.length > 0) {
      handleClientTabKey(event);
      return;
    } else if (vm.matterMenuOpen && vm.matters.length > 0) {
      handleMatterTabKey(event);
      return;
    }
  }
  
  // For other keys, check focus + menu state
  if (vm.clientFocused && vm.clientMenuOpen) {
    handleClientKeydown(event);
  } else if (vm.matterFocused && vm.matterMenuOpen) {
    handleMatterKeydown(event);
  }
};

const handleTabKeySelection = (event: KeyboardEvent, type: 'client' | 'matter'): void => {
  event.preventDefault(); // Prevent default Tab behavior to allow selection
  
  // Find the currently highlighted item based on type
  const highlightedItem = type === 'client' 
    ? findHighlightedClientItem() 
    : findHighlightedMatterItem();
  
  if (highlightedItem) {
    // Select the highlighted item
    if (type === 'client') {
      props.cm.client = highlightedItem;
      clientChange();
    } else {
      props.cm.matter = highlightedItem;
      matterChange();
    }
  }
  
  // Close the menu
  if (type === 'client') {
    vm.clientMenuOpen = false;
  } else {
    vm.matterMenuOpen = false;
  }
  
  // Move focus to next element
  setTimeout(() => {
    const currentElement = document.activeElement;
    const form = currentElement?.closest('form') || document;
    const focusableElements = form.querySelectorAll(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    );
    const currentIndex = Array.from(focusableElements).indexOf(currentElement);
    
    if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
      const nextElement = focusableElements[currentIndex + 1];
      nextElement.focus();
    }
  }, UI_DELAY_MS);
};

const handleClientTabKey = (event: KeyboardEvent): void => {
  handleTabKeySelection(event, 'client');
};

const handleMatterTabKey = (event: KeyboardEvent): void => {
  handleTabKeySelection(event, 'matter');
};

const handleClientKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    // Track highlighted index for Tab key selection
    updateClientHighlightedIndex(event.key === 'ArrowDown' ? 1 : -1);
  } else if (event.key === 'Enter') {
    const highlightedItem = findHighlightedClientItem();
    if (highlightedItem) {
      event.preventDefault();
      props.cm.client = highlightedItem;
      clientChange();
    }
  } else if (event.key === 'Escape') {
    event.preventDefault();
    vm.clientMenuOpen = false;
    vm.clientHighlightedIndex = -1;
  }
};

const handleMatterKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    // Track highlighted index for Tab key selection
    updateMatterHighlightedIndex(event.key === 'ArrowDown' ? 1 : -1);
  } else if (event.key === 'Enter') {
    const highlightedItem = findHighlightedMatterItem();
    if (highlightedItem) {
      event.preventDefault();
      props.cm.matter = highlightedItem;
      matterChange();
    }
  } else if (event.key === 'Escape') {
    event.preventDefault();
    vm.matterMenuOpen = false;
    vm.matterHighlightedIndex = -1;
  }
};

const findHighlightedItem = (type: 'client' | 'matter'): ClientOptionModel | MatterOptionModel | null => {
  const items = type === 'client' ? vm.clients : vm.matters;
  const highlightedIndex = type === 'client' ? vm.clientHighlightedIndex : vm.matterHighlightedIndex;
  
  // First try to find the menu in the DOM
  const menu = document.querySelector('.v-overlay--active .v-list') || 
               document.querySelector('.v-menu__content .v-list') ||
               document.querySelector('[role="listbox"]');
  
  if (menu) {
    // Look for the highlighted/focused list item - try multiple selectors in priority order
    let highlightedElement = 
      menu.querySelector('.v-list-item--active') ||           // Most common Vuetify active state
      menu.querySelector('.v-list-item--focused') ||          // Focused state
      menu.querySelector('.v-list-item[tabindex="0"]') ||     // Keyboard focused
      menu.querySelector('.v-list-item:focus') ||             // Browser focus
      menu.querySelector('.v-list-item[aria-selected="true"]') || // ARIA selected
      menu.querySelector('.v-list-item--highlighted');        // Custom highlighted
    
    if (highlightedElement) {
      // Get all list items and find the index of the highlighted one
      const allListItems = Array.from(menu.querySelectorAll('.v-list-item:not(.v-list-item--disabled)'));
      const domHighlightedIndex = allListItems.indexOf(highlightedElement);
      
      // Make sure the index is valid and within our items array
      if (domHighlightedIndex >= 0 && domHighlightedIndex < items.length) {
        return items[domHighlightedIndex];
      }
    }
  }
  
  // Fallback: if DOM detection failed, use our tracked index
  if (highlightedIndex >= 0 && highlightedIndex < items.length) {
    return items[highlightedIndex];
  }
  
  // Last resort: if no highlighting detected, don't select anything
  return null;
};

const findHighlightedClientItem = (): ClientOptionModel | null => {
  return findHighlightedItem('client') as ClientOptionModel | null;
};

const findHighlightedMatterItem = (): MatterOptionModel | null => {
  return findHighlightedItem('matter') as MatterOptionModel | null;
};

const updateHighlightedIndex = (type: 'client' | 'matter', direction: number): void => {
  const items = type === 'client' ? vm.clients : vm.matters;
  
  if (items.length === 0) {
    if (type === 'client') {
      vm.clientHighlightedIndex = -1;
    } else {
      vm.matterHighlightedIndex = -1;
    }
    return;
  }
  
  // Update the appropriate index
  if (type === 'client') {
    vm.clientHighlightedIndex += direction;
  } else {
    vm.matterHighlightedIndex += direction;
  }
  
  // Get current index for wrap-around logic
  const currentIndex = type === 'client' ? vm.clientHighlightedIndex : vm.matterHighlightedIndex;
  
  // Wrap around
  if (currentIndex >= items.length) {
    if (type === 'client') {
      vm.clientHighlightedIndex = 0;
    } else {
      vm.matterHighlightedIndex = 0;
    }
  } else if (currentIndex < 0) {
    if (type === 'client') {
      vm.clientHighlightedIndex = items.length - 1;
    } else {
      vm.matterHighlightedIndex = items.length - 1;
    }
  }
};

const updateClientHighlightedIndex = (direction: number): void => {
  updateHighlightedIndex('client', direction);
};

const updateMatterHighlightedIndex = (direction: number): void => {
  updateHighlightedIndex('matter', direction);
};

const clearClient = async () => {
  vm.clientQuery.search = '';
  vm.matterQuery.search = '';
  try {
    if (useClientSearch.value) {
      // Large firm: vm.clients is always a server-filtered subset, so always re-fetch.
      await fetchClients();
    } else {
      // Small firm: vm.clients already holds the complete list, no refetch needed.
      // Explicitly clear the selection so fetchMatters queries without a clientId.
      props.cm.client = null;
      props.cm.matter = null;
      vm.matters = [];
    }
    await fetchMatters();
    emit('matterChange');
  } catch (error) {
    // Handle cancellation errors silently - they're expected when clearing
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      return;
    }
    // Re-throw other errors
    throw error;
  }
};

const clearMatter = async (): Promise<void> => {
  if (props.cm.client) {
  } else {
    await resetClientsAndMatters();
  }
};

const resetClientsAndMatters = async (): Promise<void> => {
  // Always wipe vm.clients: it may represent a prior query's filtered subset
  // and clientCount is query-specific so cannot reliably guard this decision.
  vm.clients = [];
  vm.matters = [];
  props.cm.client = null;
  props.cm.matter = null;
};

const fetchClients = async (preserveSelections = false) => {
  // Cancel previous client search and create new controller
  abortController.create('clientSearch');

  vm.clientsLoading = true;
  
  // Only reset if we're not preserving selections
  if (!preserveSelections) {
    await resetClientsAndMatters();
  } else {
    // Just clear the dropdown lists, preserve selections
    vm.clients = [];
    vm.matters = [];
  }
  
  try {
    const clientCount = await cmOptionsStore.getClientCount(vm.clientQuery, abortController.getSignal('clientSearch'));
    if (clientCount < itemSearchThreshold) {
      await loadClientOptions();
    } else {
      if (!preserveSelections) {
        vm.clients = [];
      }
      // For large client counts, if we have a selected client, this allows the dropdown to expand
      if (preserveSelections && props.cm.client?.id) {
        vm.clients = [props.cm.client];
      }
    }
  } catch (error) {
    if (error.name === 'CanceledError') {
      return;
    }
    throw error;
  } finally {
    vm.clientsLoading = false;
  }
};

const fetchMatters = async (onLoad?: boolean, fetchFromMatters?: boolean): Promise<void> => {
  // Cancel previous matter search and create new controller
  abortController.create('matterSearch');

  if (!onLoad) {
    props.cm.matter = null;
  }
  vm.matterQuery.clientId = props.cm.client?.id;
  vm.mattersLoading = true;
  
  try {
    const matterCount = await cmOptionsStore.getMatterCount(vm.matterQuery, abortController.getSignal('matterSearch'));
    if (matterCount < itemSearchThreshold) {
      await loadMatterOptions(fetchFromMatters);
    } else {
      vm.matters = [];
      vm.mattersLoading = false;
    }
  } catch (error) {
    if (error.name === 'CanceledError') {
      return;
    }
    vm.mattersLoading = false;
    throw error;
  }
};

const loadClientOptions = async () => {
  const clients = await cmOptionsStore.listClientOptions(
    vm.clientQuery,
    abortController.getSignal('clientSearch')
  );
  vm.clients = clients;
  if (props.cm.client) {
    props.cm.client = vm.clients.find((c) => c.id === props.cm.client.id);
  }
};

const clientSearch = async (newVal: string, oldVal: string): Promise<void> => {
  if (vm.clearingMatterSearch) {
    return;
  }
  if (newVal.length > 0) {
    if (newVal !== props.cm.client?.label) {
      await fetchClients();
    }
  } else if (!props.cm.client?.id) {
    await fetchClients();
  }
};

const matterSearch = async (newVal: string, oldVal: string): Promise<void> => {
  if (vm.clearingMatterSearch) {
    return;
  }
  if (newVal.length > 0) {
    if (newVal !== props.cm.matter?.label) {
      await fetchMatters(false, true);
    }
  } else if (!props.cm.matter?.id) {
    await fetchMatters();
  }
};

const loadMatterOptions = async (fetchFromMatter?: boolean): Promise<void> => {
  vm.mattersLoading = true;
  vm.matterQuery.clientId = props.cm.client?.id;
  if (vm.clearingMatterSearch) {
    vm.matterQuery.search = '';
  }
  
  try {
    const matters = await cmOptionsStore.listMatterOptions(
      vm.matterQuery,
      abortController.getSignal('matterSearch')
    );
    vm.matters = matters;
    if (!fetchFromMatter) {
      if (props.cm.matter) {
        props.cm.matter = vm.matters.find((m) => m.id === props.cm.matter.id);
      }
      if (vm.matters.length === 1) {
        props.cm.matter = vm.matters[0];
        matterChange();
      }
    } else if (vm.clearingMatterSearch) {
      // Only our own input: by now the user may have moved on to another field.
      const focused = document.activeElement;
      if (!props.clientOnly && focused instanceof HTMLElement && root.value?.contains(focused)) {
        focused.blur();
      }
      vm.clearingMatterSearch = false;
    }
  } catch (error) {
    if (error.name === 'CanceledError') {
      return;
    }
    throw error;
  } finally {
    vm.mattersLoading = false;
  }
};

const clientChange = async (): Promise<void> => {
  if (vm.clearingMatterSearch) {
    return;
  }
  if (!props.cm.client) {
    await resetClientsAndMatters();
    matterChange();
    return;
  }
  props.cm.matter = null;
  if (useClientSearch.value) {
    vm.clients = [];
  }
  vm.matters = [];
  vm.clientQuery.search = '';
  vm.matterQuery.search = '';
  await fetchMatters();
  if (props.emitOnAnyChange) emit('matterChange');
};

const matterChange = async (): Promise<void> => {
  if (vm.clearingMatterSearch) {
    return;
  }
  if (!props.cm.matter?.id) {
    props.cm.matter = null;
    if (props.cm.client) {
      if (vm.matters.length === 1) {
        props.cm.matter = vm.matters[0];
      } else {
        await fetchMatters();
      }
    }
    emit('matterChange');
    return;
  }
  if (
    props.cm.matter?.clientId &&
    props.cm.matter.clientId !== props.cm.client?.id
  ) {
    vm.clearingMatterSearch = true;
    
    // Cancel previous client details request and create new controller
    abortController.create('clientDetails');
    
    try {
      const client = await cmOptionsStore.getClient(
        props.cm.matter.clientId,
        abortController.getSignal('clientDetails')
      );
      props.cm.client = client;
    } catch (error) {
      if (error.name === 'CanceledError') {
        return;
      }
      throw error;
    }
    
    await loadMatterOptions(true);
  }
  emit('matterChange');
};

const addNewClient = (): void => {
  emit('addNewClient', vm.clientQuery.search, 'clientMatterSearch');
};

const addNewMatter = (): void => {
  emit('addNewMatter', vm.matterQuery.search, 'clientMatterSearch');
};

__expose({
  clearClient,
  async refreshMatters() {
    // Store current search to restore later
    const currentSearch = vm.matterQuery.search;

    // Clear the cache to force fresh data
    cmOptionsStore.$patch({ matterOptions: [], matterCount: 0 });

    // Temporarily clear search to get full matter list
    vm.matterQuery.search = '';

    // Use the existing fetchMatters logic
    await fetchMatters(true); // true = onLoad, keeps current selection

    // Restore the original search term
    vm.matterQuery.search = currentSearch;
  },
});

return (_ctx: any,_cache: any) => {
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: _normalizeClass(["flex justify-between tw", __props.horizontal ? 'flex-col' : 'flex-row'])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["w-full", __props.horizontal ? '' : 'pr-2'])
    }, [
      (!__props.hideClientLabel)
        ? (_openBlock(), _createElementBlock("label", _hoisted_1, [
            _cache[10] || (_cache[10] = _createTextVNode("Client ", -1)),
            _withDirectives(_createElementVNode("span", _hoisted_2, "*", 512), [
              [_vShow, __props.required]
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(_component_v_autocomplete, {
        modelValue: __props.cm.client,
        "onUpdate:modelValue": [
          _cache[0] || (_cache[0] = ($event: any) => ((__props.cm.client) = $event)),
          _cache[4] || (_cache[4] = ($event: any) => (clientChange($event)))
        ],
        items: vm.clients,
        density: "compact",
        "item-title": "label",
        "item-value": "id",
        loading: vm.clientsLoading,
        class: "at-choose-client remove-outline",
        "return-object": "",
        clearable: "",
        searchable: "",
        placeholder: useClientSearch.value ? 'Search Clients' : 'Select Client',
        variant: "outlined",
        "hide-details": __props.hideDetails,
        rules: __props.clientRules,
        "hide-no-data": false,
        search: vm.clientQuery.search,
        "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((vm.clientQuery.search) = $event)),
        searchModifiers: { sync: true },
        "onClick:clear": _cache[2] || (_cache[2] = ($event: any) => (clearClient())),
        onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (clickClient()), ["prevent"])),
        onBlur: onClientBlur,
        "onUpdate:menu": onClientMenuUpdate,
        onFocus: onClientFocus,
        "data-cy": __props.clientDataCy,
        ref: "clientAutocomplete",
        "menu-props": { contentClass: 'sticky-menu' },
        disabled: __props.disabled
      }, _createSlots({
        "no-data": _withCtx(() => [
          _createVNode(_component_v_list_item, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_list_item_title, { class: "text-sm text-medium-emphasis" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(clientNoDataMessage.value), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 2
      }, [
        (__props.showAddClientMatter)
          ? {
              name: "append-item",
              fn: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_component_v_list_item, {
                    onClick: addNewClient,
                    class: "cursor-pointer"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, { class: "text-primary font-weight-medium" }, {
                        default: _withCtx(() => [
                          _createVNode(LlIcon, {
                            icon: "mdi-plus",
                            color: "primary",
                            class: "mr-1 pb-1"
                          }),
                          _createTextVNode(" " + _toDisplayString(vm.clientQuery.search
                    ? `Create New "${vm.clientQuery.search}"`
                    : 'Create New Client'), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["modelValue", "items", "loading", "placeholder", "hide-details", "rules", "search", "data-cy", "disabled"])
    ], 2),
    (!__props.clientOnly)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["w-full", __props.horizontal ? 'pt-2' : 'pl-2'])
        }, [
          _createElementVNode("label", _hoisted_4, [
            _createTextVNode(_toDisplayString(_unref(terms)?.Matter) + " ", 1),
            _withDirectives(_createElementVNode("span", _hoisted_5, "*", 512), [
              [_vShow, __props.required]
            ])
          ]),
          _createVNode(_component_v_autocomplete, {
            "data-cy": __props.matterDataCy,
            modelValue: __props.cm.matter,
            "onUpdate:modelValue": [
              _cache[5] || (_cache[5] = ($event: any) => ((__props.cm.matter) = $event)),
              _cache[9] || (_cache[9] = ($event: any) => (matterChange($event)))
            ],
            items: vm.matters,
            loading: vm.mattersLoading,
            density: "compact",
            "item-title": "label",
            "item-value": "id",
            class: "at-choose-matter remove-outline",
            "return-object": "",
            clearable: vm.matters.length > 1,
            placeholder: useMatterSearch.value ? 'Search Matters' : 'Select Matter',
            variant: "outlined",
            "hide-details": __props.hideDetails,
            "hide-no-data": false,
            search: vm.matterQuery.search,
            "onUpdate:search": _cache[6] || (_cache[6] = ($event: any) => ((vm.matterQuery.search) = $event)),
            searchModifiers: { sync: true },
            "onClick:clear": _cache[7] || (_cache[7] = ($event: any) => (clearMatter())),
            onClick: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (clickMatter()), ["prevent"])),
            onBlur: onMatterBlur,
            "onUpdate:menu": onMatterMenuUpdate,
            onFocus: onMatterFocus,
            rules: __props.matterRules,
            ref: "matterAutocomplete",
            "menu-props": { contentClass: 'sticky-menu' },
            disabled: __props.disabled
          }, _createSlots({
            "no-data": _withCtx(() => [
              _createVNode(_component_v_list_item, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, { class: "text-sm text-medium-emphasis" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(matterNoDataMessage.value), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 2
          }, [
            (__props.showAddClientMatter)
              ? {
                  name: "append-item",
                  fn: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_component_v_list_item, {
                        onClick: addNewMatter,
                        class: "cursor-pointer"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, { class: "text-primary font-weight-medium" }, {
                            default: _withCtx(() => [
                              _createVNode(LlIcon, {
                                icon: "mdi-plus",
                                color: "primary",
                                class: "mr-1 pb-1"
                              }),
                              _createTextVNode(" " + _toDisplayString(vm.matterQuery.search
                    ? `Create New "${vm.matterQuery.search}"`
                    : `Create New ${_unref(terms)?.Matter}`), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["data-cy", "modelValue", "items", "loading", "clearable", "placeholder", "hide-details", "search", "rules", "disabled"])
        ], 2))
      : _createCommentVNode("", true)
  ], 2))
}
}

})